/**
 * Pure dot/seen logic for the What's New pill — kept out of the component so
 * it's testable without a DOM (utils/whatsNewBadge.test.js).
 *
 * Ruling (Nina 09-15): the dot shows for EVERYONE, first-timers included —
 * no returning-visitor gate. It shows while the newest entry is inside the
 * badge window AND the viewer hasn't opened the sheet since that entry
 * landed. Opening the sheet stamps last-seen = newest entry date.
 *
 * localStorage stays in the component wrapped in try/catch; these functions
 * take plain values so a blocked storage read just means lastSeen = null,
 * which correctly shows the dot.
 */

/** Newest entry date string, or null for an empty list. Entries are kept
 *  newest-first in data/whatsNew.js but this doesn't assume it. */
export function newestEntryDate(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  return entries.reduce(
    (max, e) => (e && typeof e.date === 'string' && e.date > max ? e.date : max),
    ''
  ) || null;
}

/**
 * Whether the pill shows the green unread dot.
 * @param entries     the WHATS_NEW_ENTRIES array
 * @param lastSeen    'YYYY-MM-DD' stamped at last sheet-open, or null/undefined
 * @param today       'YYYY-MM-DD' for now (caller derives; keeps this pure)
 * @param windowDays  entries older than this never badge (default 14)
 */
export function shouldShowBadge({ entries, lastSeen, today, windowDays = 14 }) {
  const newest = newestEntryDate(entries);
  if (!newest || typeof today !== 'string') return false;
  // Window check without Date-parsing pitfalls: ISO date strings compare
  // lexicographically, so shift `today` back windowDays via UTC epoch math
  // on the date-only string (no TZ component to skew).
  const t = Date.parse(today + 'T00:00:00Z');
  if (Number.isNaN(t)) return false;
  const cutoff = new Date(t - windowDays * 86400000).toISOString().slice(0, 10);
  if (newest < cutoff) return false;
  if (newest > today) return false; // future-dated entry = staged, never badge early
  if (lastSeen && typeof lastSeen === 'string' && lastSeen >= newest) return false;
  return true;
}

/** True when the entry is newer than what the viewer had seen when the sheet
 *  opened — drives the green row tint. null lastSeen = everything is new. */
export function entryIsUnseen(entry, lastSeenAtOpen) {
  if (!entry || typeof entry.date !== 'string') return false;
  if (!lastSeenAtOpen || typeof lastSeenAtOpen !== 'string') return true;
  return entry.date > lastSeenAtOpen;
}
